/**
 * accessResolver - 客户端 L1 预判层（文档 §9.2 / §7.2）
 *
 * 客户端快速预判权限，避免明显非法请求发到服务端。
 *
 * 安全边界（4 项不得）：
 * 1. 不得查询 D1（客户端无数据库访问）
 * 2. 不得自行推导私人资源 owner（owner 校验由服务端 L4 做）
 * 3. 不得作为最终 API 授权（L3 accessPolicyService 是真相源）
 * 4. 不得推导推荐套餐（仅 allow/deny/partial，不含升级建议）
 *
 * 输入：PrototypeState + AccessAction + resource hint
 * 输出：allow / partial / login / paywall
 *
 * 设计原则：
 * - 与 accessPolicyService 共享 ACTION_REGISTRY
 * - 客户端预判可以"假阳性"（allow 但服务端 deny），但不能"假阴性"（deny 但服务端 allow）
 *   - 假阳性：请求发到服务端被拒，UX 稍差但安全
 *   - 假阴性：用户被错误拒绝，功能不可用
 *   - 因此客户端预判保守：不确定时返回 allow（让服务端裁决）
 */

import { ACTION_REGISTRY } from '../../domain/accessRegistry';
import type { AccessAction, AccessDecision } from '../../domain/types/accessTypes';
import { getPlanEntitlements, planStore } from './planStore';
import { FREE_CHAPTER_COUNT } from '../../domain/access/publicChapterPolicy';

export type ClientAccessDecision = AccessDecision;

export interface ClientAccessContext {
	/** 是否已登录 */
	isLoggedIn: boolean;
	/** 当前套餐 */
	plan: 'visitor' | 'free' | 'lite' | 'pro' | 'max';
	/** 当前已认证用户；仅用于把登录恢复意图绑定到本次用户会话。 */
	userId?: string;
	/** Server-projected unified AI balance. Unknown means defer to L3. */
	quotaAvailable?: number;
	/** 资源提示（可选） */
	resource?: {
		/** 资源类型（可选 - parser 不一定推断得到，如 setMode 上下文） */
		type?: 'chapter' | 'aiJob' | 'privateCourse';
		index?: number;
		/** 是否为当前用户的资源（客户端已知，如 private_ai 课程） */
		isOwner?: boolean;
	};
}

/**
 * resolveClientAccess - 客户端 L1 预判
 *
 * @param action AccessAction
 * @param context ClientAccessContext
 * @returns ClientAccessDecision
 */
export function resolveClientAccess(action: AccessAction, context: ClientAccessContext): ClientAccessDecision {
	const spec = ACTION_REGISTRY[action];
	if (!spec) {
		// 未知 action：保守返回 allow，让服务端裁决
		return { ok: true, kind: 'allow', action };
	}

	// Step 2: requiresSession
	if (spec.requiresSession && !context.isLoggedIn) {
		return { ok: false, kind: 'login', code: 'authentication_required', action };
	}

	// Bootstrap may still be loading after a direct deep-link navigation. An empty
	// client catalog is not entitlement evidence: defer to L3 rather than turning
	// its fail-closed placeholder into a false client-side denial.
	if (!planStore.getAll()?.[context.plan]) {
		return { ok: true, kind: 'allow', action };
	}

	// Step 3: capability 检查
	if (spec.capability) {
		const entitlements = getPlanEntitlements(context.plan);
		const hasCap = entitlements[spec.capability as keyof typeof entitlements] === true;
		if (!hasCap) {
			// 已登录无 capability -> paywall；未登录 -> login_required（已在 Step 2 处理）
			return { ok: false, kind: 'paywall', code: 'entitlement_required', action, capability: spec.capability };
		}
	}

	// Step 4: ownerCheck（客户端只能做 hint 级判断，真相源在服务端）
	// 如果客户端已知 isOwner=false，可以提前 deny；但保守起见，让服务端裁决
	// （客户端预判不自行推导 owner）

	// Step 5: quota 检查
	if (spec.quota) {
		if (context.quotaAvailable !== undefined && context.quotaAvailable <= 0) {
			return {
				ok: false,
				kind: 'quota',
				code: 'quota_exceeded',
				action,
				quota: { key: 'aiCreateBalance', available: 0, resetAt: null },
			};
		}
	}

	// Step 6: partial 正文
	if (spec.partial && context.resource?.type === 'chapter') {
		const entitlements = getPlanEntitlements(context.plan);
		if (spec.fullAccessCapability && entitlements[spec.fullAccessCapability as keyof typeof entitlements] === true) {
			return { ok: true, kind: 'allow', action };
		}

		const textAccess = entitlements.publicTextAccess;
		if (!spec.fullAccessCapability && textAccess === 'full') return { ok: true, kind: 'allow', action };

		// metered: 第 1-3 章全文，第 4+ 章 25%
		const idx = context.resource.index ?? 0;
		if (idx <= FREE_CHAPTER_COUNT) {
			return { ok: true, kind: 'allow', action };
		}
		return {
			ok: true,
			kind: 'partial',
			action: 'chapter.read',
			visibleRatio: 0.25,
			gate: context.isLoggedIn ? 'paywall' : 'login',
		};
	}

	// Step 7: 媒体访问
	if (spec.mediaKind && context.resource?.type === 'chapter') {
		const entitlements = getPlanEntitlements(context.plan);
		const mediaAccess = entitlements.publicMediaAccess;
		if (mediaAccess === 'all') {
			return { ok: true, kind: 'allow', action };
		}
		const idx = context.resource.index ?? 0;
		if (mediaAccess === 'free_chapters' && idx <= FREE_CHAPTER_COUNT) {
			return { ok: true, kind: 'allow', action };
		}
		// none 或 free_chapters 超出免费范围
		// 已登录 -> paywall；未登录 -> login_required
		if (!context.isLoggedIn) {
			return { ok: false, kind: 'login', code: 'authentication_required', action };
		}
		return { ok: false, kind: 'paywall', code: 'entitlement_required', action, capability: 'publicMediaAccess' };
	}

	// P6: privateCourse.read - 客户端已知 isOwner=true 时 allow
	if (action === 'privateCourse.read' && context.resource?.isOwner) {
		return { ok: true, kind: 'allow', action };
	}

	// 默认 allow（保守，让服务端裁决）
	return { ok: true, kind: 'allow', action };
}
